import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const TaskCreateModal = ({ isOpen, onClose, onSuccess, initialData = {} }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client: '',
    assignedTo: [],
    assignedGroups: [],
    deadline: '',
    status: 'pending',
    verificationTypes: []
  });
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [taskPrefillings, setTaskPrefillings] = useState([]);
  const [selectedPrefillingId, setSelectedPrefillingId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(client =>
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, clients]);

  useEffect(() => {
    if (userSearchTerm) {
      const filtered = users.filter(user =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
      setShowUserDropdown(true);
    } else {
      setFilteredUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearchTerm, users]);

  useEffect(() => {
    if (groupSearchTerm) {
      const filtered = groups.filter(group =>
        group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
      );
      setFilteredGroups(filtered);
      setShowGroupDropdown(true);
    } else {
      setFilteredGroups([]);
      setShowGroupDropdown(false);
    }
  }, [groupSearchTerm, groups]);

  const fetchInitialData = async () => {
    try {
      const [clientsRes, usersRes, groupsRes, verificationTypesRes] = await Promise.all([
        api.get('/master-data'),
        api.get('/users'),
        api.get('/groups'),
        api.get('/admin/verification-types')
      ]);

      if (clientsRes.data.success) {
        setClients(clientsRes.data.data || []);
      } else {
        setClients(clientsRes.data || []);
      }
      setUsers(usersRes.data.data || []);
      setGroups(groupsRes.data || []);
      setVerificationTypes(verificationTypesRes.data || []);
      try {
        const prefillingsRes = await api.get('/admin/task-prefillings');
        setTaskPrefillings(Array.isArray(prefillingsRes.data) ? prefillingsRes.data : prefillingsRes.data?.data || []);
      } catch (_) {
        setTaskPrefillings([]);
      }

      const defaultFormData = {
        name: '',
        description: '',
        client: '',
        assignedTo: [],
        assignedGroups: [],
        deadline: '',
        status: 'pending',
        verificationTypes: []
      };
      const mergedData = { ...defaultFormData, ...initialData };
      setFormData(mergedData);
      setSelectedPrefillingId('');

      if (mergedData.client) {
        const clientsList = clientsRes.data.success ? (clientsRes.data.data || []) : (clientsRes.data || []);
        const selectedClient = clientsList.find(c => c._id === mergedData.client);
        if (selectedClient) setSearchTerm(selectedClient.clientName);
      } else {
        setSearchTerm('');
      }
    } catch (error) {
      toast.error('Failed to fetch initial data');
      console.error('Error fetching initial data:', error);
    }
  };

  const handlePrefillingChange = (e) => {
    const id = e.target.value;
    setSelectedPrefillingId(id);
    if (!id) return;
    const prefilling = taskPrefillings.find(p => p._id === id);
    if (!prefilling) return;
    let deadlineStr = '';
    if (prefilling.deadlineDays != null && !isNaN(prefilling.deadlineDays)) {
      const d = new Date();
      d.setDate(d.getDate() + Number(prefilling.deadlineDays));
      deadlineStr = d.toISOString().split('T')[0];
    }
    setFormData(prev => ({
      ...prev,
      name: prefilling.taskName || prev.name,
      description: prefilling.description || prev.description,
      client: prefilling.client?._id || '',
      assignedTo: (prefilling.assignedTo || []).map(u => u._id),
      assignedGroups: (prefilling.assignedGroups || []).map(g => g._id),
      deadline: deadlineStr,
      status: prefilling.status || 'pending',
      verificationTypes: (prefilling.verificationTypes || []).map(v => v._id)
    }));
    setSearchTerm(prefilling.client?.clientName || '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientSelect = (clientId) => {
    setFormData(prev => ({
      ...prev,
      client: clientId
    }));
    const selectedClient = clients.find(c => c._id === clientId);
    setSearchTerm(selectedClient ? selectedClient.clientName : '');
    setShowSuggestions(false);
  };

  const handleUserSelect = (userId) => {
    if (userId && !formData.assignedTo.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        assignedTo: [...prev.assignedTo, userId]
      }));
    }
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleGroupSelect = (groupId) => {
    if (groupId && !formData.assignedGroups.includes(groupId)) {
      setFormData(prev => ({
        ...prev,
        assignedGroups: [...prev.assignedGroups, groupId]
      }));
    }
    setGroupSearchTerm('');
    setShowGroupDropdown(false);
  };

  const removeUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.filter(id => id !== userId)
    }));
  };

  const removeGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: prev.assignedGroups.filter(id => id !== groupId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Format verification types to match the expected schema
      const formattedVerificationTypes = formData.verificationTypes.map(typeId => {
        const verificationType = verificationTypes.find(vt => vt._id === typeId);
        return {
          value: verificationType ? verificationType.name : '',
          id: typeId
        };
      });

      const taskData = {
        ...formData,
        verificationTypes: formattedVerificationTypes
      };

      await api.post('/tasks', taskData);
      toast.success('Task created successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedUsers = () => {
    return formData.assignedTo.map(userId => {
      const user = users.find(u => u._id === userId);
      return user ? { id: userId, name: `${user.firstName} ${user.lastName}` } : null;
    }).filter(Boolean);
  };

  const getSelectedGroups = () => {
    return formData.assignedGroups.map(groupId => {
      const group = groups.find(g => g._id === groupId);
      return group ? { id: groupId, name: group.name } : null;
    }).filter(Boolean);
  };

  const getSelectedClient = () => {
    return clients.find(c => c._id === formData.client);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Task</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
                {/* Prefilling */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Use prefilling</label>
                  <select
                    value={selectedPrefillingId}
                    onChange={handlePrefillingChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    {taskPrefillings.map(p => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Task Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter task name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter task description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    required
                  />
                </div>

                {/* Client Selection (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search for a client..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {showSuggestions && filteredClients.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredClients.map((client) => (
                          <div
                            key={client._id}
                            onClick={() => handleClientSelect(client._id)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            {client.clientName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {getSelectedClient() && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <span className="text-sm text-blue-800">
                        Selected: {getSelectedClient().clientName}
                      </span>
                    </div>
                  )}
                </div>

                {/* Assigned Users */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign To Users
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      placeholder="Search for users..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {showUserDropdown && filteredUsers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredUsers.map((user) => (
                          <div
                            key={user._id}
                            onClick={() => handleUserSelect(user._id)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            {user.firstName} {user.lastName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {getSelectedUsers().length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {getSelectedUsers().map((user) => (
                        <span
                          key={user.id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {user.name}
                          <button
                            type="button"
                            onClick={() => removeUser(user.id)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assigned Groups */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign To Groups
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={groupSearchTerm}
                      onChange={(e) => setGroupSearchTerm(e.target.value)}
                      placeholder="Search for groups..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {showGroupDropdown && filteredGroups.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredGroups.map((group) => (
                          <div
                            key={group._id}
                            onClick={() => handleGroupSelect(group._id)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            {group.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {getSelectedGroups().length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {getSelectedGroups().map((group) => (
                        <span
                          key={group.id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          {group.name}
                          <button
                            type="button"
                            onClick={() => removeGroup(group.id)}
                            className="ml-1 text-green-600 hover:text-green-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Verification Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Types
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {verificationTypes.map((type) => (
                      <label key={type._id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.verificationTypes.includes(type._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                verificationTypes: [...prev.verificationTypes, type._id]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                verificationTypes: prev.verificationTypes.filter(id => id !== type._id)
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{type.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Deadline */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deadline <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCreateModal;

